import { SharedFileRequestJobType } from "@wingspanhq/shared-files/dist/lib/interfaces";
import type {
  insuranceCoverageApiSpec,
  insuranceImportApiSpec
} from "@wingspanhq/wingspan-api-types";

import {
  VALIDATION_ITEM_WARNING_STATUS,
  ValidationItemProps,
  ValidationItemStatus
} from "../../../components/AIValidationWidget/ValidationItem";

/**
 * Two additions to the V1 insurance contract that `@wingspanhq/wingspan-api-types`
 * 1.30.1 predates: a coverage's `warnings` and the `Warning` job status on
 * imports and monitors. The V1 REST layer passes both through unchanged, so
 * they are declared here until the app moves to a types release that carries
 * them. A warning is a certificate check that did not pass but did not block
 * the import; today the only such check is the named-insured match.
 */
export type InsuranceCoverageWarning = {
  title: string;
  message: string;
};

export type InsuranceCoverageWithWarnings =
  insuranceCoverageApiSpec.InsuranceCoverage & {
    warnings?: InsuranceCoverageWarning[];
  };

export const selectorInsuranceCoverageWarnings = (
  coverage: insuranceCoverageApiSpec.InsuranceCoverage | undefined
): InsuranceCoverageWarning[] =>
  (coverage as InsuranceCoverageWithWarnings | undefined)?.warnings ?? [];

/** The wire carries no key for the check, so the named-insured one is known by title. */
const NAMED_INSURED_WARNING_PATTERN = /named insured/i;

export const isNamedInsuredWarning = (
  warning: Pick<InsuranceCoverageWarning, "title">
): boolean => NAMED_INSURED_WARNING_PATTERN.test(warning.title ?? "");

export type InsuranceWarningAudience = "contractor" | "client";

/**
 * The mismatch sentence with both names, built from structured fields rather
 * than parsed out of the model's message, degrading gracefully when a name is
 * not available on this screen.
 */
export const getNamedInsuredMismatchText = ({
  namedInsured,
  profileName,
  audience
}: {
  namedInsured?: string | null;
  profileName?: string | null;
  audience: InsuranceWarningAudience;
}): string => {
  const onCertificate = namedInsured
    ? `The named insured on this certificate, ${namedInsured},`
    : "The named insured on this certificate";

  if (audience === "contractor") {
    const profile = profileName
      ? ` your profile name, ${profileName}.`
      : " your profile name.";
    return `${onCertificate} does not match${profile} You can submit this policy for your client to review or upload another certificate.`;
  }

  const profile = profileName
    ? ` the contractor's profile name, ${profileName}.`
    : " the contractor's profile name.";
  return `${onCertificate} does not match${profile}`;
};

/** The named insured the extractor read off the certificate, off the import's Extraction job. */
export const selectorInsuranceImportNamedInsured = (
  jobs: insuranceImportApiSpec.InsuranceImportJob[] | undefined
): string | undefined =>
  jobs?.find(job => job.type === "Extraction" && !!job.extractedFields)
    ?.extractedFields?.namedInsured?.name ?? undefined;

/**
 * Warning rows ahead of everything else, otherwise in their given order: the
 * check the client is being asked to rule on leads the list.
 */
export const sortWarningsFirst = <T extends { status?: ValidationItemStatus }>(
  items: T[]
): T[] => [
  ...items.filter(item => item.status === VALIDATION_ITEM_WARNING_STATUS),
  ...items.filter(item => item.status !== VALIDATION_ITEM_WARNING_STATUS)
];

/**
 * A coverage's warnings as rows for the shared validation widget, in the
 * Warning state rather than as passed checks.
 */
export const getCoverageWarningValidationItems = (
  coverage: insuranceCoverageApiSpec.InsuranceCoverage | undefined
): ValidationItemProps[] =>
  selectorInsuranceCoverageWarnings(coverage).map(
    warning =>
      ({
        title: warning.title,
        description: "",
        type: SharedFileRequestJobType.Validation,
        status: VALIDATION_ITEM_WARNING_STATUS,
        statusReason: warning.message
      }) as ValidationItemProps
  );
